import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Cloud, Sun, CloudRain, Thermometer, Wind, Compass, Calendar, TrendingUp, CloudLightning, CloudSnow, CloudDrizzle } from "lucide-react";
import { Link } from "wouter";

// Vaal Dam / Aloe Fjord coordinates
const LAT = -26.89;
const LON = 28.12;

interface CurrentWeather {
  temperature_2m: number;
  apparent_temperature: number;
  relative_humidity_2m: number;
  wind_speed_10m: number;
  wind_direction_10m: number;
  surface_pressure: number;
  visibility: number;
  uv_index: number;
  weather_code: number;
}

interface DailyWeather {
  time: string[];
  weather_code: number[];
  temperature_2m_max: number[];
  temperature_2m_min: number[];
  wind_speed_10m_max: number[];
  precipitation_probability_max: number[];
  uv_index_max: number[];
}

interface OpenMeteoResponse {
  current: CurrentWeather;
  daily: DailyWeather;
}

function wmoDescription(code: number): string {
  if (code === 0) return "Clear sky";
  if (code <= 2) return "Partly cloudy";
  if (code === 3) return "Overcast";
  if (code <= 48) return "Foggy";
  if (code <= 55) return "Drizzle";
  if (code <= 65) return "Rain";
  if (code <= 77) return "Snow";
  if (code <= 82) return "Rain showers";
  if (code <= 86) return "Snow showers";
  if (code <= 99) return "Thunderstorm";
  return "Unknown";
}

function WmoIcon({ code, size = 24 }: { code: number; size?: number }) {
  const cls = `text-orange-500`;
  if (code === 0 || code === 1) return <Sun size={size} className={cls} />;
  if (code <= 3) return <Cloud size={size} className={cls} />;
  if (code <= 48) return <Cloud size={size} className={cls} />;
  if (code <= 55) return <CloudDrizzle size={size} className={cls} />;
  if (code <= 65) return <CloudRain size={size} className={cls} />;
  if (code <= 77) return <CloudSnow size={size} className={cls} />;
  if (code <= 82) return <CloudRain size={size} className={cls} />;
  if (code <= 99) return <CloudLightning size={size} className={cls} />;
  return <Cloud size={size} className={cls} />;
}

function windDirection(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function uvLevel(uvi: number): { label: string; color: string } {
  if (uvi <= 2) return { label: "Low", color: "text-green-600" };
  if (uvi <= 5) return { label: "Moderate", color: "text-yellow-600" };
  if (uvi <= 7) return { label: "High", color: "text-orange-600" };
  if (uvi <= 10) return { label: "Very High", color: "text-red-600" };
  return { label: "Extreme", color: "text-purple-600" };
}

function boatingCondition(windKph: number, visibilityM: number): { label: string; color: string; bg: string } {
  if (windKph > 30 || visibilityM < 5000)
    return { label: "Poor", color: "text-red-600", bg: "bg-red-50" };
  if (windKph > 20)
    return { label: "Moderate", color: "text-yellow-600", bg: "bg-yellow-50" };
  return { label: "Good", color: "text-green-600", bg: "bg-green-50" };
}

export default function Weather() {
  const [data, setData] = useState<OpenMeteoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${LAT}&longitude=${LON}` +
      `&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m,surface_pressure,visibility,uv_index` +
      `&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,precipitation_probability_max,uv_index_max` +
      `&wind_speed_unit=ms&timezone=Africa%2FJohannesburg&forecast_days=7`;

    fetch(url)
      .then((r) => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then((json) => {
        if (!json.current || !json.daily) throw new Error('Unexpected shape');
        setData(json);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Weather Dashboard</h1>
          <Card>
            <CardContent className="p-8">
              <p className="text-gray-500">Weather data temporarily unavailable. Please try again later.</p>
              <Link href="/">
                <button className="mt-4 bg-orange-300 hover:bg-orange-400 text-black px-6 py-2 rounded-lg">
                  Return Home
                </button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const cur = data.current;
  const daily = data.daily;
  const windKph = Math.round(cur.wind_speed_10m * 3.6);
  const uv = uvLevel(cur.uv_index);
  const boating = boatingCondition(windKph, cur.visibility);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-16">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Weather Dashboard</h1>
          <p className="text-xl text-gray-600">Live conditions for Aloe Fjord, Vaal Dam</p>
        </div>

        {/* Current + Boating */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <Card className="lg:col-span-2 bg-gradient-to-br from-blue-50 to-orange-50 border-orange-200">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Thermometer className="text-orange-500" size={28} />
                Current Conditions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 mb-6">
                <WmoIcon code={cur.weather_code} size={64} />
                <div>
                  <div className="text-5xl font-bold text-gray-800 mb-2">
                    {Math.round(cur.temperature_2m)}°C
                  </div>
                  <div className="text-lg text-gray-600 capitalize mb-1">
                    {wmoDescription(cur.weather_code)}
                  </div>
                  <div className="text-gray-500">
                    Feels like {Math.round(cur.apparent_temperature)}°C
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Wind className="text-orange-500" size={16} />
                  <div>
                    <div className="font-medium">{windKph} km/h</div>
                    <div className="flex items-center gap-1">
                      <Compass size={14} className="text-orange-500" />
                      <span className="text-sm font-medium">{windDirection(cur.wind_direction_10m)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500 text-xs mb-1">Humidity</div>
                  <div className="font-medium">{cur.relative_humidity_2m}%</div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500 text-xs mb-1">Pressure</div>
                  <div className="font-medium">{Math.round(cur.surface_pressure)} hPa</div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500 text-xs mb-1">Visibility</div>
                  <div className="font-medium">{Math.round(cur.visibility / 1000)} km</div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-1">
                <Sun size={16} className="text-orange-500" />
                <span className={`text-sm font-medium ${uv.color}`}>
                  UV {Math.round(cur.uv_index)} — {uv.label}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Boating Conditions */}
          <Card className={`${boating.bg} border-orange-200`}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="text-orange-500" size={20} />
                Boating Conditions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${boating.color} mb-4`}>{boating.label}</div>
              <div className="space-y-2 text-sm text-gray-600">
                <div>Wind: {windKph} km/h — {windKph > 20 ? "Strong winds, use caution" : "Suitable for boating"}</div>
                <div>Visibility: {Math.round(cur.visibility / 1000)} km — {cur.visibility < 5000 ? "Limited visibility" : "Good visibility"}</div>
                <div>UV Level: {Math.round(cur.uv_index)} — Remember sun protection</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 7-Day Forecast */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Calendar className="text-orange-500" size={28} />
              7-Day Extended Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              {daily.time.map((dateStr, i) => {
                const date = new Date(dateStr);
                const dayName = i === 0 ? "Today" : date.toLocaleDateString("en-ZA", { weekday: "short" });
                const fullDate = date.toLocaleDateString("en-ZA", { month: "short", day: "numeric" });
                const windDayKph = Math.round(daily.wind_speed_10m_max[i] * 3.6);

                return (
                  <Card key={dateStr} className="bg-white/60 border-orange-100 hover:bg-white/80 transition-colors">
                    <CardContent className="p-4 text-center">
                      <div className="text-sm font-medium text-gray-700 mb-1">{dayName}</div>
                      <div className="text-xs text-gray-500 mb-3">{fullDate}</div>
                      <div className="flex justify-center mb-3">
                        <WmoIcon code={daily.weather_code[i]} size={36} />
                      </div>
                      <div className="text-lg font-bold text-gray-800 mb-1">
                        {Math.round(daily.temperature_2m_max[i])}°
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        {Math.round(daily.temperature_2m_min[i])}°
                      </div>
                      <div className="text-xs text-gray-600 capitalize mb-3">
                        {wmoDescription(daily.weather_code[i])}
                      </div>
                      <div className="space-y-1 text-xs text-gray-500">
                        <div>Wind: {windDayKph} km/h</div>
                        {daily.precipitation_probability_max[i] > 0 && (
                          <div>Rain: {daily.precipitation_probability_max[i]}%</div>
                        )}
                        <div>UV: {Math.round(daily.uv_index_max[i])}</div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Insights */}
        <Card className="bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-200">
          <CardHeader>
            <CardTitle className="text-xl">Boating Weather Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Best Boating Days</h4>
                <div className="space-y-1 text-gray-600">
                  {daily.time.slice(0, 3).map((dateStr, i) => {
                    const kph = Math.round(daily.wind_speed_10m_max[i] * 3.6);
                    const label = i === 0 ? "Today" : new Date(dateStr).toLocaleDateString("en-ZA", { weekday: "long" });
                    if (kph <= 20) {
                      return (
                        <div key={dateStr} className="flex justify-between">
                          <span>{label}</span>
                          <span className="text-green-600">Good conditions</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">UV Protection Needed</h4>
                <div className="space-y-1 text-gray-600">
                  {daily.time.slice(0, 3).map((dateStr, i) => {
                    const label = i === 0 ? "Today" : new Date(dateStr).toLocaleDateString("en-ZA", { weekday: "long" });
                    if (daily.uv_index_max[i] > 5) {
                      return (
                        <div key={dateStr} className="flex justify-between">
                          <span>{label}</span>
                          <span className="text-orange-600">High UV — {Math.round(daily.uv_index_max[i])}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Rain Probability</h4>
                <div className="space-y-1 text-gray-600">
                  {daily.time.slice(0, 3).map((dateStr, i) => {
                    const label = i === 0 ? "Today" : new Date(dateStr).toLocaleDateString("en-ZA", { weekday: "long" });
                    if (daily.precipitation_probability_max[i] > 30) {
                      return (
                        <div key={dateStr} className="flex justify-between">
                          <span>{label}</span>
                          <span className="text-blue-600">{daily.precipitation_probability_max[i]}% chance</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
